import ConfigCmsApi from '../configs/ConfigCmsApi';
import Cookies from 'js-cookie';
import APICmsCatalogs from './cms-api-catalogs';

const headers = () => ({ Authorization: Cookies.get('adminToken') });

const getPermissions = () => ConfigCmsApi.get('/agencies/permissions', { headers: headers() }).then(response => {
  if (typeof response.data?.can_manage_agencies !== 'boolean') throw new Error('Agency permission unavailable');
  return response.data.can_manage_agencies;
});

const getStats = () => ConfigCmsApi.get('/agencies/stats', { headers: headers() });

const getAgencies = (params) => ConfigCmsApi.get('/agencies/', { headers: headers(), params });

const getAgency = agencyId => ConfigCmsApi.get(`/agencies/${agencyId}`, { headers: headers() });
const updateAgency = (agencyId, data) => ConfigCmsApi.patch(`/agencies/${agencyId}`, data, { headers: headers() });
const activityResponse = request => request.then(response => {
  if (response.data?.status !== true) throw new Error(response.data?.error?.code || 'Partner status request failed');
  return response.data;
});
const activateAgency = agencyId => activityResponse(ConfigCmsApi.patch(`/agencies/${agencyId}/activate`, {}, { headers: headers() }));
const deactivateAgency = (agencyId, reason, details) => activityResponse(ConfigCmsApi.patch(`/agencies/${agencyId}/deactivate`, { reason, details }, { headers: headers() }));
const getDeactivationReasons = () => ConfigCmsApi.get('/agencies/deactivation-reasons', { headers: headers() }).then(response => {
  if (!Array.isArray(response.data?.reasons) || response.data.reasons.some(reason => !reason || typeof reason.value !== 'string' || !reason.value || typeof reason.label !== 'string')) throw new Error('Suspension reasons unavailable');
  return response.data.reasons;
});
const getLastDeactivation = agencyId => ConfigCmsApi.get(`/agencies/${agencyId}/last-deactivation`, { headers: headers() }).then(response => {
  if (response.data?.status === false || !Object.prototype.hasOwnProperty.call(response.data || {}, 'deactivation')) throw new Error('Suspension history unavailable');
  return response.data.deactivation;
});

const getAgencyUsers = (id, params) => ConfigCmsApi.get(`/agencies/${id}/users`, { headers: headers(), params }).then(response => {
  const body = response.data;
  if (body?.status === false || !Array.isArray(body?.users) || !body?.pagination || !Number.isInteger(body.pagination.total_count)) {
    throw new Error('User list unavailable');
  }
  return body;
});

const getManagers = () => ConfigCmsApi.get('/agencies/managers', { headers: headers() });

const setAgencyUserStatus = (agencyId, userId, status, expectedStatus) => ConfigCmsApi.patch(`/agencies/${agencyId}/users/${userId}/status`, { status, expected_status: expectedStatus }, { headers: headers() }).then(response => {
  const body = response.data;
  if (body?.id !== userId || body?.agency_id !== Number(agencyId) || body?.status !== status || typeof body?.changed !== 'boolean') throw new Error('Status was not verified. Refresh before retrying.');
  return body;
});

const impersonate = (agencyId) =>
  ConfigCmsApi.post(`/agencies/${agencyId}/impersonate`, {}, { headers: headers() });

const impersonateUser = (agencyId, userId) =>
  ConfigCmsApi.post(`/agencies/${agencyId}/users/${userId}/impersonate`, {}, { headers: headers() });

const createAgency = (data) => ConfigCmsApi.post('/agencies/', data, { headers: headers() });

const getPresentationUrls = (agencyId) =>
  ConfigCmsApi.get(`/agencies/${agencyId}/presentation-urls`, { headers: headers() });

const uploadPresentations = (agencyId, files) => {
  const formData = new FormData();
  files.forEach((file) => formData.append('files[]', file));

  return ConfigCmsApi.post(`/agencies/${agencyId}/presentation-urls`, formData, { headers: headers() });
};

const removePresentation = (agencyId, url) =>
  ConfigCmsApi.delete(`/agencies/${agencyId}/presentation-urls`, { headers: headers(), data: { url } });

// ---------------------------------------------------------------------------------------------
// Partner commission (PRO-12830)
//
// Every response is unwrapped here so the page only ever sees plain payloads or a normalized
// CommissionApiError. cms-api (AgencyBundle) returns success payloads as bare JSON and errors as a
// serialized Result — `{ status: false, error: { code, data } }` — with HTTP 200, so `status ===
// false` is checked on every response. A 4xx carrying the same body is handled too, in case the
// status is ever passed through; 401 is left to the shared refresh interceptor. Adjust only this
// block if the contract changes.
// ---------------------------------------------------------------------------------------------

export class CommissionApiError extends Error {
  constructor({ code = 'unknown', data = {}, status = null } = {}) {
    super(code);
    this.code = code;
    this.data = data || {};
    this.status = status;
  }

  // Error codes arrive prefixed by the service that raised them (`agency_commission_rule.…`,
  // `agency.settings.…`, `agency.commission_terms.…`), so match on the tail.
  is(suffix) {
    return this.code === suffix || this.code.endsWith(`.${suffix}`);
  }

  // `agency_commission_rule.period_overlap` → data.overlapping_rule_ids
  get overlappingRuleIds() {
    const ids = this.data.overlapping_rule_ids || this.data.rule_ids || this.data.ids || [];
    return Array.isArray(ids) ? ids : [];
  }

  // `*.validation_failed` / `agency.settings.invalid_value` → data.violations as
  // { field: code } (e.g. { commission_value: 'percentage_above_100', 'procedure.id': 'invalid' }).
  // A list of { property_path|field, message } is accepted too. Nested paths collapse to the
  // top-level form field ('procedure.id' → 'procedure').
  get fieldErrors() {
    const raw = this.data.violations || this.data.details || {};
    const pairs = Array.isArray(raw)
      ? raw.map((v) => [v.property_path || v.propertyPath || v.field || v.path || '', v.message || v.code || 'invalid'])
      : Object.entries(raw);

    return pairs.reduce((acc, [path, message]) => {
      const field = String(path).replace(/^\[|\]$/g, '').split(/[.[]/)[0];
      if (field && !acc[field]) acc[field] = String(message);
      return acc;
    }, {});
  }
}

const toCommissionError = (body, status) => {
  const error = body && body.error;
  if (error && typeof error === 'object') {
    return new CommissionApiError({ code: error.code || 'unknown', data: error.data, status });
  }
  if (typeof error === 'string') {
    return new CommissionApiError({ code: error, data: body.data || {}, status });
  }
  if (status === 404) {
    return new CommissionApiError({ code: 'not_found', status });
  }
  return null;
};

const unwrapCommission = (request) =>
  request.then(
    (response) => {
      const body = response && response.data;
      if (body && body.status === false) {
        throw toCommissionError(body, response.status) || new CommissionApiError({ status: response.status });
      }
      return body;
    },
    (e) => {
      const response = e && e.response;
      const normalized = response ? toCommissionError(response.data, response.status) : null;
      throw normalized || e;
    }
  );

const getCommissionTerms = (agencyId) =>
  unwrapCommission(ConfigCmsApi.get(`/agencies/${agencyId}/commission-terms`, { headers: headers() }));

// data: { commission_standard, commission_manual_review, excluded_categories, contract_currency_id }
const saveCommissionTerms = (agencyId, data) =>
  unwrapCommission(ConfigCmsApi.put(`/agencies/${agencyId}/commission-terms`, data, { headers: headers() }));

const getCommissionRules = (agencyId) =>
  unwrapCommission(ConfigCmsApi.get(`/agencies/${agencyId}/commission-rules`, { headers: headers() }))
    .then((body) => (body && body.commission_rules) || []);

// Upsert: a rule with `id` is updated, without it — created.
const saveCommissionRule = (agencyId, rule) =>
  unwrapCommission(ConfigCmsApi.put(`/agencies/${agencyId}/commission-rules`, rule, { headers: headers() }))
    .then((body) => (body && body.commission_rule) || null);

const removeCommissionRule = (agencyId, ruleId) =>
  unwrapCommission(ConfigCmsApi.delete(`/agencies/${agencyId}/commission-rules/${ruleId}`, { headers: headers() }));

const getCommissionCurrencies = () =>
  unwrapCommission(ConfigCmsApi.get('/agencies/commission/currencies', { headers: headers() }))
    .then((body) => (Array.isArray(body) ? body : (body && body.currencies) || []));

// Procedures reuse the cms-api catalog endpoints the CMS already calls instead of a dedicated
// commission catalog (cms-api has none):
//   GET /data/operations?name=<q>&is_short=1&perPage=20  → { operations: [{ id, name: { en, ru, … } }], meta }
//   GET /data/diagnostics?name=<q>&perPage=20            → { diagnostics: [{ id, name: { en, … } }], meta }
// Both also accept ids[] to look up already selected procedures. Normalized to [{ type, id, name }].
const PROCEDURE_CATALOGS = {
  operation: { request: APICmsCatalogs.getOperations, key: 'operations', params: { is_short: 1 } },
  diagnostic: { request: APICmsCatalogs.getDiagnostics, key: 'diagnostics', params: {} },
};

const localizedName = (name) => {
  if (!name) return null;
  if (typeof name === 'string') return name;
  return name.en || name.ru || Object.values(name).find(Boolean) || null;
};

const fetchCommissionProcedures = (type, extraParams) => {
  const catalog = PROCEDURE_CATALOGS[type];
  if (!catalog) return Promise.resolve([]);

  return catalog.request({ perPage: 20, ...catalog.params, ...extraParams }).then((response) => {
    const items = (response && response.data && response.data[catalog.key]) || [];
    return items.map((item) => ({ type, id: item.id, name: localizedName(item.name) }));
  });
};

const searchCommissionProcedures = ({ type, q }) => fetchCommissionProcedures(type, q ? { name: q } : {});

// axios serializes an array param as ids[]=1&ids[]=2.
const getCommissionProceduresByIds = ({ type, ids }) =>
  ids && ids.length ? fetchCommissionProcedures(type, { ids, perPage: ids.length }) : Promise.resolve([]);

const contractResponse = request => request.then(response => {
  if (response.data?.status === false) throw new Error(response.data.error?.code || 'Contract request failed');
  return response.data;
});
const getContracts = id => contractResponse(ConfigCmsApi.get(`/agencies/${id}/contracts`, { headers: headers() })).then(body => {
  if (!Array.isArray(body.files)) throw new Error('Contract list unavailable');
  return body.files;
});
const uploadContracts = (id, files, comment) => {
  const data = new FormData(); files.forEach(file => data.append('files[]', file)); data.append('comment', comment);
  return contractResponse(ConfigCmsApi.post(`/agencies/${id}/contracts`, data, { headers: headers() }));
};
const editContract = (id, fileId, comment) => contractResponse(ConfigCmsApi.patch(`/agencies/${id}/contracts/${fileId}`, { comment }, { headers: headers() }));
const removeContract = (id, fileId) => contractResponse(ConfigCmsApi.delete(`/agencies/${id}/contracts/${fileId}`, { headers: headers() }));
const downloadContract = (id, fileId) => contractResponse(ConfigCmsApi.get(`/agencies/${id}/contracts/${fileId}/download`, { headers: headers() })).then(body => {
  if (!body.url) throw new Error('Contract download unavailable'); return body.url;
});

export default {
  getPermissions,
  getContracts, uploadContracts, editContract, removeContract, downloadContract,
  getAgency,
  updateAgency,
  activateAgency,
  deactivateAgency,
  getDeactivationReasons,
  getLastDeactivation,
  getStats,
  getAgencies,
  getManagers,
  getAgencyUsers,
  setAgencyUserStatus,
  impersonate,
  impersonateUser,
  createAgency,
  getPresentationUrls,
  uploadPresentations,
  removePresentation,
  getCommissionTerms,
  saveCommissionTerms,
  getCommissionRules,
  saveCommissionRule,
  removeCommissionRule,
  getCommissionCurrencies,
  searchCommissionProcedures,
  getCommissionProceduresByIds,
};
